import { useEffect, useMemo, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { api } from '../api';
import type { LeasePremiseUse } from '../types';
import { Modal } from '../components/Modal';
import { PremiseUseForm } from '../components/PremiseUseForm';

const helper = createColumnHelper<LeasePremiseUse>();

export const PremiseUsesPage = () => {
  const [rows, setRows] = useState<LeasePremiseUse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
  const [insertOpen, setInsertOpen] = useState(false);
  const [editing, setEditing] = useState<LeasePremiseUse | null>(null);

  const load = async () => {
    setLoading(true);
    setRows(await api.leasePremiseUses());
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, []);

  const columns = useMemo(
    () => [
      helper.accessor('id', { header: 'ID', size: 60 }),
      helper.accessor('name', { header: 'Name' }),
      helper.accessor('active', {
        header: 'Status',
        cell: ({ getValue }) =>
          getValue() ? (
            <span className="tag ok">Active</span>
          ) : (
            <span className="tag muted">Legacy</span>
          ),
      }),
      helper.accessor('uses', { header: 'Leases', size: 80 }),
      helper.display({
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => setEditing(row.original)}>Edit</button>
            <button
              className="danger"
              onClick={async () => {
                if (!confirm(`Delete ${row.original.name}?`)) return;
                const result = await api.deletePremiseUse(row.original.id);
                if (!result.ok) {
                  alert(
                    `Cannot delete "${row.original.name}" — it is used by ${result.inUse} lease(s). Reassign those leases first.`,
                  );
                  return;
                }
                await load();
              }}
            >
              Delete
            </button>
          </div>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter: search },
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',
  });

  return (
    <div className="page">
      <h1>Premise Uses</h1>
      <div className="toolbar">
        <input
          className="search"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="spacer" />
        <button className="primary" onClick={() => setInsertOpen(true)}>
          + Insert premise use
        </button>
      </div>
      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} onClick={h.column.getToggleSortingHandler()}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {h.column.getIsSorted() === 'asc'
                        ? ' ▲'
                        : h.column.getIsSorted() === 'desc'
                          ? ' ▼'
                          : ''}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((r) => (
                <tr key={r.id}>
                  {r.getVisibleCells().map((c) => (
                    <td key={c.id}>{flexRender(c.column.columnDef.cell, c.getContext())}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {insertOpen && (
        <Modal title="Insert premise use" onClose={() => setInsertOpen(false)}>
          <PremiseUseForm
            save={(input, force) => api.createPremiseUse(input, force)}
            onSuccess={async () => {
              setInsertOpen(false);
              await load();
            }}
            onCancel={() => setInsertOpen(false)}
            submitLabel="Create"
          />
        </Modal>
      )}
      {editing && (
        <Modal title={`Edit ${editing.name}`} onClose={() => setEditing(null)}>
          <PremiseUseForm
            initial={editing}
            save={(input, force) => api.updatePremiseUse(editing.id, input, force)}
            onSuccess={async () => {
              setEditing(null);
              await load();
            }}
            onCancel={() => setEditing(null)}
            submitLabel="Save"
          />
        </Modal>
      )}
    </div>
  );
};
